import React, { useState, useEffect, useMemo } from 'react';

// Custom
import { PPDocument, PPTextType } from '@/lib/prostorniPlanoviHelper';
import { escapeRegex } from '@/lib/helpers';

export interface IPPSearch {
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  matches: Array<{ id: string }>;
  currentMatchIndex: number;
  goToMatch: (direction: 1 | -1) => void;
}

interface UsePPSearchProps {
  doc: PPDocument;
  onExpandItems: (ids: Array<string>) => void;
  treeRef: React.RefObject<HTMLDivElement>;
}

const flatTree = (node: PPTextType): Array<PPTextType> => {
  if (Array.isArray(node.children) && node.children.length > 0) {
    return [node, ...node.children.map((x) => flatTree(x)).flat()];
  }
  return [node];
};

// hook
function usePPSearch(props: UsePPSearchProps): IPPSearch {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [matches, setMatches] = useState<{ id: string }[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const { doc, onExpandItems, treeRef } = props;

  const itemArray = useMemo(() => doc.map((x) => flatTree(x)).flat(), [doc]);

  // --- Navigate matches ---
  const goToMatch = (direction: 1 | -1) => {
    if (!matches.length) return;
    setCurrentMatchIndex((prev) => {
      const next = (prev + direction + matches.length) % matches.length;
      return next;
    });
  };

  const getTreeItemHierarchyIds = (id: string) => {
    let item = itemArray.find((x) => x.id.toString() === id);

    const ids: Array<string> = [];
    if (item) {
      ids.push(item.id.toString());
      while (item && item.parent_id) {
        const parentId: string = item.parent_id.toString();
        ids.push(parentId);
        item = itemArray.find((x) => x.id.toString() === parentId);
      }
    }

    return ids;
  };

  useEffect(() => {
    if (currentMatchIndex < 0 || currentMatchIndex >= matches.length) return;

    const match = matches[currentMatchIndex];
    const hIds = getTreeItemHierarchyIds(match.id);

    // First expand the items, then scroll after expansion is complete
    const expandAndScroll = async () => {
      // Expand the hierarchy
      onExpandItems(hIds);

      // Wait for the DOM to update (use a small delay)
      // eslint-disable-next-line no-promise-executor-return
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Find the element after expansion
      const el = treeRef.current?.querySelector(`[data-id="${match.id}"]`);
      if (!el) return;

      el.scrollIntoView({ behavior: 'smooth' });

      /* // Get the sidebar content scrollable element
      const treeView = document.querySelector('.sidebar-right #mui-tree-view-2');
      if (!sidebarContent) return;

      // Calculate the element's position relative to the sidebar
      const elementRect = el.getBoundingClientRect();
      const sidebarRect = sidebarContent.getBoundingClientRect();
      const relativeTop = elementRect.top - sidebarRect.top + sidebarContent.scrollTop;

      // Scroll to position with 48px offset from top
      sidebarContent.scrollTo({
        top: relativeTop - 48,
        behavior: 'smooth',
      }); */
    };

    expandAndScroll();
  }, [currentMatchIndex, matches]);

  // --- Find matches ---
  useEffect(() => {
    if (!searchTerm) {
      setMatches([]);
      setCurrentMatchIndex(0);
      return;
    }

    const found: { id: string }[] = [];

    const traverse = (items?: PPTextType[]) => {
      if (!items) return;
      items.forEach((item) => {
        if (new RegExp(escapeRegex(searchTerm), 'i').test(item.value)) {
          found.push({ id: item.id.toString() });
        }
        // @ts-ignore
        traverse(item.children);
      });
    };
    // @ts-ignore
    traverse(doc);

    setMatches(found);
    setCurrentMatchIndex(found.length > 0 ? 0 : -1);
  }, [searchTerm, doc]);

  return {
    searchTerm,
    setSearchTerm,
    matches,
    currentMatchIndex,
    goToMatch,
  }
}

export default usePPSearch;
